/**
 * 이상한 방 - 매치 페이즈 전환 · 타임아웃 · 구획별 마감 채움
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(라운드 구조: 꾸미기 → 바꾸기 → 외우기 → 수색 → 리빌)
 *            `이상한방-상태모델-v0.1.md` 9.5절(싱글은 방을 외우는 튜토리얼) · 11.3절(페이즈·타이머는 서버가 쥔다)
 *            로드맵 모델 2.1절(절대 시각 → dt) · 2.6절(체인저 로테이션 · CPU 체인저)
 *
 * ## 절대 시각을 받고 dt로 돈다
 *
 * 매치의 `tick`은 `atMs`(절대 시각)를 싣는다. 호스트가 사람 이벤트 직전에 `tick(atMs)`를 선적용해
 * 마감 경계를 정확히 처리하기 위해서다. 리듀서 안에서는 `dtMs = atMs - clockMs`를 뽑아 **dt 기반 리프**
 * (`originVisit`·`phaseElapsedMs`)를 싱글과 똑같이 돈다. 시계를 읽는 곳은 어디에도 없다.
 *
 * ## 틱 병합 불변 · 한 틱에 페이즈는 하나만
 *
 * `tick(t1); tick(t2) ≡ tick(t2)`가 규칙이다(테스트가 지킨다). 모든 타이머가 "0으로 클램프 / ≥ 비교"
 * 꼴이고 마감 초과분을 다음 페이즈로 넘기지 않으므로 **한 구간 안에서** 성립한다. 한 틱에 페이즈를
 * 하나만 넘기는 이유도 같다 - 두 마감을 한 틱에 넘기면 둘째 페이즈의 경과가 초과분을 물려받아야 하고,
 * 그 순간 병합 불변이 깨진다. 호스트는 어차피 초 단위로 틱을 보내므로 페이즈가 한 틱 늦는 일은
 * 리빌 → 결과처럼 사람이 못 느끼는 자리에서만 일어난다.
 *
 * ## 아무도 손댈 사람이 없으면 바꾸기 길이가 0이다
 *
 * 사람이 만들 시간이 필요할 때만 기다린다. 봇 판의 반복 속도가 개발 속도이고, 전원이 이웃인 판의 "60초"는
 * 사람에게 빈 화면일 뿐이다. 길이 0의 페이즈를 상태에 세우지 않고 **라운드 시작에서 곧장 외우기로** 간다 -
 * 길이 0 페이즈를 두면 그것을 넘기는 데 틱이 하나 더 들고(위 규칙), 그 틱 하나가 병합 불변을 깬다
 * (큰 틱 하나는 바꾸기에 멈추고 작은 틱 둘은 외우기까지 간다).
 *
 * ## 작업대는 `matchChange.ts`가 채우고, 마감은 여기 있다
 *
 * `applyChange`·`undoChange`는 각자의 작업대(`desks[id]`)만 바꾸고 페이즈를 건드리지 않는다(M4 `matchChange.ts`).
 * 바꾸기가 끝나는 길은 셋 - 시간 종료 · 방장의 건너뛰기 · **전원의** `commitChanges` - 이고 **셋 다 `finishChange` 하나**를
 * 지난다.
 *
 * ## 마감 채움은 구획별로 한다 (M11 규칙 8)
 *
 * 예산을 남기고 끝낸 사람의 **구획 안에서만** 생성기가 채우고, 채운 것의 주인은 그 사람이다(점수 대상). 구획 하나짜리
 * 평면도(`matchZones.zonePlanOf`)를 생성기에 주는 것이 그 수단이다 - 생성기는 이미 `plan.rooms`를 배분 축으로 쓰므로
 * 새 인자 없이 기존 규칙 그대로 그 안에서만 만든다. 그래서 라운드 총 변화 수는 **항상 좌석 수 × 인당 예산**이다.
 * 채움은 `touched`를 건드리지 않고 사람이 만든 변화는 그대로 앞에 남는다 - 마감 순간 끊겨 있어도 같은 경로다
 * (작업대가 서 있는 동안 `hasHumanChanger`를 다시 묻지 않는다. 60초 동안 만든 것을 끊김 한 번으로 버리면 안 된다).
 *
 * ## 방 규칙은 `config.room`이 진실이다
 *
 * 인당 이상한 곳 수(`changeCount`)와 허용 동사는 방장이 로비에서 `configure`로 고친 값(`state.config.room`)이고,
 * `deps.tuning`은 그 기본값을 공급했을 뿐이다(`matchState.roomConfigFrom` - `?perPlayer=`가 그 기본값이다).
 * 생성기에는 `roundTuning`으로 덮어 넘긴다 - 튜닝을 그대로 넘기면 `configure`가 저장만 되는 장식이 되고 진실이 둘이 된다.
 *
 * **여럿이에서 `config.room.changeCount`는 인당 예산이다.** 싱글의 같은 이름 값(라운드 총량 12)과 뜻이 다른 이유는
 * 여럿이의 총량이 사람 수의 함수이기 때문이다 - 총량을 방장이 정하면 여섯이 앉은 판에서 한 사람 몫이 두 개가 되는
 * 설정이 가능해지고, 그 판은 "고칠 것이 없는 사람"을 만든다(M11 대전제).
 */

import { generateBaseRoom } from '../../core/roomGen'
import { MS_PER_SECOND } from '../../core/time'
import type { PlayerId, RoomState } from '../../core/types'
import { NO_COMBO } from '../combo'
import { NO_ORIGIN_VISIT } from '../originVisit'
import { revealDurationMs } from '../phaseClock'
import { normalizeWager } from '../raise'
import type { Tuning } from '../tuning'
import { emptyRoomFor, fillToFloor } from './matchDecorate'
import { deskChanges, freshDesks, mergeDrafts } from './matchDesk'
import { EMPTY_DOOR, tickOrigin } from './matchOrigin'
import { settleRound } from './matchScore'
import {
  allHuntersOut,
  canSkipPhase,
  chain,
  hasHumanChanger,
  idle,
  mapPlayers,
  matchPlan,
  matchZones,
  reachableUnfound,
  roomOptionsFor,
  seatsInOrder,
  skipVotesNeeded,
  unfoundChanges,
} from './matchSelectors'
import type {
  ChangeDesk,
  MatchChange,
  MatchDeps,
  MatchEffect,
  MatchEndReason,
  MatchPhase,
  MatchPlayer,
  MatchState,
  MatchStep,
} from './matchTypes'
import { zonePlanOf, type ZoneAssignment } from './matchZones'

/* --- 상수 ------------------------------------------------------------------------ */

/**
 * 라운드 변화 시드 접두사. `gameState.ts`의 `:a`(시도)와 같은 자리에 `:r`(라운드)이 들어간다 -
 * 방은 매치 내내 고정이고(9.5절: 방을 외우는 것이 스킬) 라운드마다 변화만 갈린다.
 */
const ROUND_SEED_PREFIX = ':r'

/** 로비의 라운드 번호. `start`가 첫 라운드(1)로 올린다. */
export const LOBBY_ROUND = 0

/**
 * 구획 채움 시드의 접두사. 라운드 시드(`${seed}:r${round}`) 뒤에 구획 id를 붙여 **구획마다 다른 수열**을 쓴다 -
 * 같은 시드로 여덟 구획을 채우면 여덟 구획이 같은 순서로 같은 동사를 받는다.
 */
const ZONE_FILL_SEED_PREFIX = ':z'

/* --- 시드 · 로테이션 -------------------------------------------------------------- */

/** 이 라운드의 변화 시드. 재현(`?seed=…`로 같은 라운드)의 근거이므로 한 함수에 둔다. */
export function roundChangeSeed(state: MatchState): string {
  return `${state.seed}${ROUND_SEED_PREFIX}${state.round}`
}

/**
 * 이 매치의 방 규칙을 얹은 튜닝(머리말 "방 규칙은 `config.room`이 진실이다"). `changeCount`는 **인당 예산**이다.
 * 생성기와 뷰(`matchView.changeCount`)가 같은 함수로 읽는다. `Set` → 배열은 `Tuning.allowedVerbs`의 모양을 맞추는 것뿐이고 순서는 삽입 순서 그대로다.
 */
export function roundTuning(state: MatchState, tuning: Tuning): Tuning {
  return {
    ...tuning,
    changeCount: state.config.room.changeCount,
    allowedVerbs: [...state.config.room.allowedVerbs],
  }
}

/** 이 라운드에 한 사람이 만들 수 있는 이상한 곳 수. 라운드 전체는 `좌석 수 × 이 값`이다(M11 규칙 2). */
export function budgetPerPlayer(state: MatchState, tuning: Tuning): number {
  return roundTuning(state, tuning).changeCount
}

/** 라운드 전체의 이상한 곳 수. 구획마다 정확히 예산만큼 나므로 곱 하나다 - 두 곳에서 세면 갈린다. */
export function totalChangeCount(state: MatchState, tuning: Tuning): number {
  return seatsInOrder(state).length * budgetPerPlayer(state, tuning)
}

/* --- 좌석 되돌리기 ---------------------------------------------------------------- */

/**
 * 라운드 자원을 처음으로. 라운드 시작과 수색 진입이 **같은 함수**를 편다 - `gameState.freshRound`·`enterHunt`가
 * 같은 필드를 되돌리는 것과 같은 이유다(한쪽만 빠뜨린 필드가 곧 "지난 판의 퍼펙트가 이번 판에 뜬다").
 * 탄창 보유량만 계정의 것이라 이어받는다.
 */
function freshSeat(player: MatchPlayer, tuning: Tuning): MatchPlayer {
  return {
    ...player,
    ammo: tuning.startAmmo,
    magazinesUsed: 0,
    combo: NO_COMBO,
    maxCombo: NO_COMBO,
    hasPerfect: false,
    // 4.3절: 관전은 라운드의 것이다. 다음 라운드 외우기 진입에서 자동 복귀한다.
    spectating: false,
    spectateTarget: null,
    origin: NO_ORIGIN_VISIT,
    raised: false,
    // 걸기는 라운드의 것이다 - 지난 라운드의 걸기가 남으면 걸지 않은 사람이 ×2를 받는다.
    wager: null,
    roundScore: 0,
  }
}

function resetSeats(state: MatchState, tuning: Tuning): MatchState {
  return mapPlayers(state, (player) => freshSeat(player, tuning))
}

/* --- 길이 ------------------------------------------------------------------------ */

/** 현재 페이즈의 길이(ms). `null`이면 시간으로 끝나지 않는다(로비 · 결과 - 사람이 눌러야 넘어간다). */
export function phaseDurationMs(state: MatchState, tuning: Tuning): number | null {
  switch (state.phase) {
    case 'lobby':
    case 'result':
      return null
    case 'decorate':
      return tuning.decorateSeconds * MS_PER_SECOND
    case 'watch':
      return tuning.watchSeconds * MS_PER_SECOND
    case 'change':
      return tuning.changeSeconds * MS_PER_SECOND
    case 'hunt':
      return tuning.huntSeconds * MS_PER_SECOND
    case 'reveal':
      return revealDurationMs(unfoundChanges(state).length, tuning)
  }
}

export function phaseRemainingMs(state: MatchState, tuning: Tuning): number | null {
  const duration = phaseDurationMs(state, tuning)
  return duration === null ? null : Math.max(0, duration - state.phaseElapsedMs)
}

/* --- 효과 묶음 -------------------------------------------------------------------- */

function all(body: MatchEffect['body']): MatchEffect {
  return { to: 'all', body }
}

function phaseEnter(state: MatchState, previous: MatchPhase): MatchEffect {
  return all({ type: 'phaseEnter', phase: state.phase, previous, round: state.round })
}

/* --- 전환 ------------------------------------------------------------------------ */

/**
 * 판이 시작되는 순간 좌석 수만큼의 평면도로 방을 다시 세운다(로드맵 M11).
 *
 * 로비에서는 방장 혼자 앉아 있으므로(`matchState.createMatch`) 그때 세운 방은 인원과 무관한 임시 방이다.
 * 로비 뷰는 `rooms`를 싣지 않아(`matchView.roomVisibility`) 그 방이 화면에 나간 적도 없다 - 여기가 진짜 방이 서는 자리다.
 *
 * **로비를 벗어나는 한 번만** 다시 세운다. 라운드가 바뀌어도 방은 같아야 하고(9.5절: 방을 외우는 것이 스킬),
 * 좌석 수도 그 뒤로는 변하지 않는다(합류는 로비에서만 · 퇴장은 좌석을 남긴다). 평면도가 이미 같으면 그대로 둔다 -
 * 고정 평면도로 도는 봇 판·테스트는 여기서 방을 두 번 만들 이유가 없다.
 */
function withRoomForSeats(state: MatchState, deps: MatchDeps): MatchState {
  const plan = matchPlan(state, deps)
  if (plan.id === state.original.templateId) return state
  const original = generateBaseRoom(plan, state.seed, deps.catalog, roomOptionsFor(plan, deps))
  return { ...state, original, mutated: original }
}

/**
 * 이 판이 꾸미기를 거치는가(M10). 방장이 켰고(`config.humanDecorate`) **놓을 손이 하나라도 있을 때**만이다 -
 * 전원이 이웃인 판의 60초는 사람에게 빈 화면이고, 그건 바꾸기 페이즈를 건너뛰는 것과 같은 판단이다(머리말).
 *
 * **첫 라운드만이다.** 방은 매치 내내 하나이고(9.5절: 방을 외우는 것이 스킬) 2라운드에 방이 다시 비면 외운 것이
 * 사라진다 - 꾸미기는 그 하나뿐인 방을 만드는 자리다.
 */
function needsDecorate(state: MatchState): boolean {
  return state.config.humanDecorate && state.round === LOBBY_ROUND && hasHumanChanger(state)
}

/**
 * 새 라운드가 외우기에 선다. `start`(로비에서)와 `nextRound`(결과에서)가 같은 함수를 쓴다.
 * 방은 다시 만들지 않는다(`original`은 매치 내내 하나) - 변화만 이 라운드의 시드로 새로 나온다.
 * 예외는 로비를 벗어나는 첫 라운드뿐이고, 그 이유는 `withRoomForSeats`에 있다.
 * `syncProxies`가 여기 있는 이유는 `gameState.roundStartEffects`와 같다: 지난 라운드의 제거 프록시가 남으면
 * 이번엔 멀쩡히 있는 물건 자리가 정답으로 잡힌다.
 */
export function startRound(state: MatchState, deps: MatchDeps): MatchStep {
  const previous = state.phase
  const seated = previous === 'lobby' ? withRoomForSeats(state, deps) : state
  const advanced: MatchState = { ...seated, round: seated.round + 1 }
  /*
   * 꾸미기를 거치는 판이면 **빈 셸에서 시작한다**(M10). 생성기가 만든 방은 버리지 않고 프리셋으로 남아
   * 마감의 하한 채움에 쓰인다(`matchDecorate.fillToFloor`) - 시드가 같으므로 그때 다시 만들면 같은 방이다.
   */
  // `seated`를 묻는다 - `advanced`는 라운드를 이미 올렸고 "첫 라운드인가"는 올리기 **전**의 값이다.
  const decorating = needsDecorate(seated)
  const room = decorating ? emptyRoomFor(seated, deps) : seated.original
  const base: MatchState = {
    // 구획 배정은 상태에 없다 - `round`가 올라간 순간 `matchZones.assignZones`의 답이 한 칸 돈다.
    ...resetSeats(advanced, deps.tuning),
    phase: 'decorate',
    phaseElapsedMs: 0,
    original: room,
    mutated: room,
    changes: [],
    found: [],
    door: EMPTY_DOOR,
    desks: null,
    predictions: {},
    unfairVotes: {},
    revealIndex: 0,
    endReason: null,
  }
  if (decorating) return { state: base, effects: roundOpening(base, previous) }
  // 꾸미기가 없는 판은 프리셋 방 그대로 바꾸기로 들어선다(3절 개정 - 외우기는 바꾸기 뒤다).
  return openChange(base, deps, previous)
}

/**
 * 라운드의 첫 화면을 세우는 효과 넉 장. 어느 페이즈로 들어서든(꾸미기·바꾸기·외우기) 같다 -
 * 방부터 손에 쥐어 주고 그 방을 화면에 올린다. `warpTo`가 빠지면 방은 도착했는데 아무도 그리지 않는다
 * (`leaveDecorate`에서 실제로 그렇게 났다).
 */
function roundOpening(next: MatchState, previous: MatchPhase): readonly MatchEffect[] {
  return [
    phaseEnter(next, previous),
    all({ type: 'roomsChanged' }),
    all({ type: 'warpTo', origin: false }),
    all({ type: 'syncProxies' }),
    all({ type: 'playSound', id: 'phase-change' }),
  ]
}

/**
 * 다음은 바꾸기다(컨셉시트 3절 개정 - 꾸미기 → **바꾸기** → 외우기 → 수색).
 *
 * ## 왜 외우기보다 먼저인가
 *
 * 처음 표는 `외우기 → 바꾸기`였다. 그러면 사람은 방을 1분 외우고 **그 기억을 1분 동안 딴짓하며 썩힌 뒤에**
 * 수색을 시작한다 - 자기 구획을 고치는 동안 머릿속에 남는 것은 방이 아니라 방금 자기가 만든 변화다.
 * 뒤집으면 외운 직후에 찾는다. 바꾸는 사람은 방을 외울 필요가 없다(자기 구획의 물건을 눌러 고르는 일이라
 * 지금 보이는 것으로 충분하다) - 그래서 잃는 것이 없다.
 *
 * 정보도 새지 않는다. 바꾸기 동안 보이는 것은 원본 + **내 초안**뿐이고(`matchView.roomsFor`) 외우기에서는
 * 원본으로 돌아온다 - 둘을 대조해 알 수 있는 것은 내가 방금 만든 내 변화뿐이다.
 *
 * ## 손댈 사람이 없으면 이 페이즈를 세우지 않는다
 *
 * 길이 0의 페이즈를 상태에 세우면 그것을 넘기는 데 틱이 하나 더 들고 병합 불변이 깨진다(머리말).
 * 작업대는 그래도 세운다 - 이웃의 구획도 마감에서 채워져야 하기 때문이다(M11 규칙 8).
 *
 * 작업대는 이웃·끊긴 좌석에도 선다(`matchDesk.freshDesks`). 진행 점(`changerProgress`)은 좌석마다 하나씩
 * 나간다 - 기다리는 화면이 "누가 몇 개"를 그 값으로 그린다.
 */
function openChange(state: MatchState, deps: MatchDeps, previous: MatchPhase): MatchStep {
  const budget = budgetPerPlayer(state, deps.tuning)
  const desks = freshDesks(state, budget)
  const seated: MatchState = { ...state, desks }
  if (!hasHumanChanger(seated)) return openWatch(seated, previous)

  const next: MatchState = { ...seated, phase: 'change', phaseElapsedMs: 0 }
  return {
    state: next,
    effects: [
      ...roundOpening(next, previous),
      ...seatsInOrder(next).map((player): MatchEffect =>
        all({ type: 'changerProgress', by: player.id, used: 0, budget }),
      ),
    ],
  }
}

/**
 * 다음은 외우기다. **정답표는 아직 만들지 않는다** - 초안은 작업대에 그대로 있고 합치는 것은 수색 진입의 일이다
 * (`enterHunt`). 그래서 이 60초 동안 클라이언트가 가진 것은 원본 하나뿐이고, 변경본은 존재하지도 않는다
 * (`matchView.MUTATED_EXISTS`). 순서를 바꾸면서 얻은 덤이다 - 전에는 외우기 뒤에 바꾸기가 와서
 * 정답표가 수색 직전에야 생겼는데, 지금도 정확히 그렇다.
 */
function openWatch(state: MatchState, previous: MatchPhase): MatchStep {
  const next: MatchState = { ...state, phase: 'watch', phaseElapsedMs: 0 }
  return { state: next, effects: roundOpening(next, previous) }
}

/**
 * 꾸미기가 끝났다(시간 종료 · 전원의 [다 놓았다] 투표). 사람이 놓은 방을 하한까지 프리셋으로 채우고
 * 그 방이 **이 매치의 방**이 된다 - 9.1절이 정한 자리다. 채움까지 끝난 뒤에야 외우기가 서는 이유는,
 * 외우기가 곧 "이 방을 외워라"인데 그 방이 그 뒤로 바뀌면 외운 것이 거짓이 되기 때문이다.
 *
 * `roomsChanged`가 여기 있는 것이 중요하다 - 꾸미기 중에는 한 물건씩 얹어 왔으므로(`matchDecorate` 머리말)
 * 채움으로 늘어난 것은 그 경로로 가지 않았다. 페이즈 경계에서 방을 통째로 다시 보내는 자리가 여기다.
 */
function leaveDecorate(state: MatchState, deps: MatchDeps): MatchStep {
  const room = fillToFloor(state.original, state, deps)
  /*
   * 효과는 `roundOpening` 넉 장이다(`openChange`가 낸다). 한때 여기서 `roomsChanged`만 내고 `warpTo`를
   * 빠뜨렸더니 브라우저에서 채운 방이 화면에 오지 않았다 - 씬은 꾸미기 중 한 물건씩 얹어 온 방을 그대로
   * 들고 있었고(`matchSessionScene.decorate`), `rooms`는 도착했지만 그리는 손이 없었다
   * (`receive`는 첫 방만 스스로 올린다). 씬을 갈아 끼우는 일은 언제나 `warpTo`의 것이다.
   */
  return openChange({ ...state, original: room, mutated: room }, deps, 'decorate')
}

/**
 * 외우기가 끝났다. 다음은 수색이고, 정답표는 그 경계에서 한 번에 생긴다(`enterHunt` - 초안 합치기 + 구획 채움).
 * 작업대는 이 페이즈 내내 상태에 남아 있다 - 세운 것은 바꾸기 진입(`openChange`)이고 여기서 소비한다.
 * 열려 있지는 않다(`matchDesk.activeDesk`가 `phase === 'change'`로 잠근다).
 */
function leaveWatch(state: MatchState, deps: MatchDeps): MatchStep {
  return enterHunt(state, deps, 'watch')
}

/**
 * 바꾸기가 끝났다(시간 종료 · 건너뛰기 · 전원의 커밋 `matchChange.onCommitChanges`). 다음은 외우기다.
 * 작업대가 없으면 계약 위반이라 던진다 - 바꾸기 페이즈는 작업대와 함께 태어난다.
 */
export function finishChange(state: MatchState, deps: MatchDeps): MatchStep {
  if (state.desks === null) throw new Error('finishChange: 바꾸기 페이즈인데 작업대가 없다')
  return openWatch(state, 'change')
}

/**
 * 구획 하나를 예산까지 채운다(M11 규칙 8). 사람이 만든 것이 이미 예산만큼이면 그대로다.
 *
 * 생성기에는 **그 구획 하나짜리 평면도**를 준다(`matchZones.zonePlanOf`) - 생성기의 배분 축이 `plan.rooms`라
 * 새 인자 없이 그 안에서만 만든다(그 함수 주석). `excludeIds`는 **전원이 건드린 것 전부**다: 남의 구획 물건은
 * 어차피 이 구획의 후보 목록에 없지만, 목록을 하나로 두면 "무엇을 빼는가"를 두 번 셀 일이 없다.
 * 채운 것의 주인은 그 구획의 주인이다 - 예산을 남긴 사람의 몫이므로 점수도 그 사람에게 간다.
 */
function fillZone(
  state: MatchState,
  deps: MatchDeps,
  zone: ZoneAssignment,
  base: RoomState,
  desk: ChangeDesk | undefined,
  excludeIds: ReadonlySet<string>,
): Readonly<{ room: RoomState; changes: readonly MatchChange[] }> {
  const made = desk?.changes ?? []
  const budget = budgetPerPlayer(state, deps.tuning)
  const remaining = budget - made.length
  const plan = zonePlanOf(matchPlan(state, deps), zone.zoneId)
  if (remaining <= 0 || plan === null) return { room: base, changes: made }

  const generated = deps.generateChanges(
    base,
    `${roundChangeSeed(state)}${ZONE_FILL_SEED_PREFIX}${zone.zoneId}`,
    { ...roundTuning(state, deps.tuning), changeCount: remaining },
    deps.catalog,
    plan,
    { excludeIds },
  )
  return {
    room: generated.mutated,
    /*
     * 채움의 id에 구획을 앞세운다 - 생성기는 매 호출 슬롯 0부터 번호를 매기므로(`chg-000`) 구획이 여럿이면
     * 같은 id가 여러 벌 난다. 그러면 `found`가 서로 다른 이상한 곳을 하나로 보고 점수가 사라진다.
     * 사람이 만든 것(`desk-<대상 id>`)과 접두사가 겹치지 않는 것도 같은 이유다(`matchChange` 머리말).
     */
    changes: [...made, ...generated.changes.map((change) => ({ ...change, id: `${zone.zoneId}-${change.id}`, owner: zone.ownerId }))],
  }
}

/**
 * 수색 시작. 여기서 방이 두 벌이 된다 - 초안 여럿을 합친 것이 밑바탕이고, 예산을 남긴 구획마다 생성기가 채운다.
 *
 * 순서가 규칙이다: 합치기(`mergeDrafts`) → 구획별 채움(`fillZone`, 좌석 순서) → 걸기 접기.
 * 채움을 합치기 **뒤에** 하는 이유는 점유 검사 때문이다 - 남이 옮겨 둔 자리에 채움이 물건을 놓으면 두 초안이
 * 합쳐질 때 한 셀에 둘이 겹친다. 합친 방 위에서 채우면 그 겹침이 구조적으로 없다.
 *
 * 변화가 0개면 던진다 - "아무리 찾아도 없는 라운드"를 여럿이 5분 동안 뒤지게 할 수는 없다.
 */
function enterHunt(state: MatchState, deps: MatchDeps, previous: MatchPhase): MatchStep {
  const desks = state.desks ?? {}
  const zones = matchZones(state, deps)
  // 5.1절(한 물건 한 번)은 방 전체에서 하나다 - 채움이 남이 건드린 물건을 다시 건드리면 판정 단위가 무너진다.
  const touched = new Set<string>(Object.values(desks).flatMap((desk) => [...desk.touched]))

  let mutated = mergeDrafts(state.original, desks, state.order)
  const changes: MatchChange[] = []
  for (const zone of zones) {
    const filled = fillZone(state, deps, zone, mutated, desks[zone.ownerId], touched)
    mutated = filled.room
    changes.push(...filled.changes)
    // 채운 것도 "이미 건드린 것"이다 - 다음 구획의 채움이 같은 물건을 고르면 한 대상에 변화 둘이 된다(5.1절).
    // 진짜 생성기는 구획 풀이 겹치지 않아 닿지 않지만, 규칙은 생성기의 성질이 아니라 여기서 지킨다.
    for (const made of filled.changes) touched.add(made.targetId)
  }
  /*
   * 구획이 없는 좌석(좌석보다 구획이 적은 판 - `matchZones` 머리말)의 초안도 버리지 않는다. 일어나지 않는 일이지만,
   * 일어났을 때 사람이 만든 것이 조용히 사라지는 쪽보다 정답표에 남는 쪽이 낫다.
   */
  const orphaned = deskChanges(desks, state.order).filter(
    (change) => !changes.some((kept) => kept.id === change.id),
  )
  changes.push(...orphaned)

  if (changes.length === 0) {
    throw new Error(`enterHunt: 변화가 0개다 (변화 시드 '${roundChangeSeed(state)}'). 허용 동사나 방 밀도를 확인하라`)
  }

  const seated = resetSeats(state, deps.tuning)
  const next: MatchState = {
    ...seated,
    /*
     * 걸기(M7)를 **실제 내 구획의 개수**로 한 번 더 접는다. 커밋은 `desk.budget`(인당 예산)으로 접었는데, 생성기가
     * `excludeIds` 때문에 채움을 다 못 채우면 그보다 작다 - 그러면 "3개는 못 찾는다"가 2개짜리 구획에서 절대
     * 맞지 않는 걸기가 된다. 점수의 분모(`matchScore.unfoundOf`)와 같은 출처여야 한다.
     * `resetSeats`가 좌석의 걸기를 지우므로 **커밋 때 굳힌 값**(`state.players`)에서 가져와 다시 얹는다.
     */
    players: Object.fromEntries(
      seatsInOrder(seated).map((player) => [
        player.id,
        {
          ...player,
          wager: normalizeWager(
            state.players[player.id]?.wager ?? null,
            changes.filter((change) => change.owner === player.id).length,
          ),
        },
      ]),
    ),
    phase: 'hunt',
    phaseElapsedMs: 0,
    mutated,
    changes,
    found: [],
    door: EMPTY_DOOR,
    desks: null,
    endReason: null,
  }
  return {
    state: next,
    effects: [
      phaseEnter(next, previous),
      all({ type: 'roomsChanged' }),
      all({ type: 'warpTo', origin: false }),
      // 변경본으로 바뀐 뒤에 그 방 기준으로 프록시를 세워야 한다(`gameState.enterHunt`와 같은 순서).
      all({ type: 'syncProxies' }),
      all({ type: 'playSound', id: 'phase-change' }),
    ],
  }
}

/**
 * 수색 종료. 원래 방에 있던 사람은 변경본으로 돌아온다(리빌은 변경본에서 연출한다) - 그 사람에게만 `warpTo`.
 * 좌석의 "안에 있다"만 지우고 쿨타임은 남긴다(`originVisit.ts` "페이즈 종료는 예외"). 문은 통째로 비운다.
 */
export function enterReveal(state: MatchState, previous: MatchPhase, reason: MatchEndReason): MatchStep {
  const holder = state.door.holder
  const cleared = mapPlayers(state, (player) =>
    player.origin.isInOriginRoom
      ? { ...player, origin: { ...player.origin, isInOriginRoom: false, originStayElapsedMs: 0 } }
      : player,
  )
  const next: MatchState = {
    ...cleared,
    phase: 'reveal',
    phaseElapsedMs: 0,
    endReason: reason,
    door: EMPTY_DOOR,
  }
  const effects: MatchEffect[] = [phaseEnter(next, previous)]
  if (holder !== null) effects.push({ to: holder, body: { type: 'warpTo', origin: false } })
  effects.push(all({ type: 'playSound', id: 'phase-change' }))
  return { state: next, effects }
}

/** 결과. 점수는 여기서 한 번 굳는다(`matchScore.settleRound`). 나가는 길은 방장의 `nextRound` 하나다. */
function enterResult(state: MatchState, deps: MatchDeps): MatchStep {
  const settled = settleRound({ ...state, phase: 'result', phaseElapsedMs: 0 }, deps.tuning)
  return {
    state: settled.state,
    effects: [
      phaseEnter(settled.state, 'reveal'),
      all({ type: 'roundScored', ranking: settled.ranking }),
      all({ type: 'playSound', id: 'round-over' }),
    ],
  }
}

/**
 * 수색이 스스로 끝났는가. 사격·관전 전환·퇴장 뒤에 부르고, 아니면 입력을 그대로 돌려준다.
 *
 * ## 더 이상 진행이 불가능하면 끝난다 (사용자 지시)
 *
 * 판단의 기준은 하나다 - **아직 찾을 사람이 남은 이상한 곳이 있는가**(`reachableUnfound`). 세 가지가 그
 * 하나로 모이는데, 이유를 셋으로 갈라 두는 이유는 결과 화면이 사실을 말해야 하기 때문이다:
 *
 * - 찾을 사람이 0명 → `all-out` (전원 관전이거나 끊겼다)
 * - 남은 것이 0개 → `all-found`
 * - 남은 것은 있는데 **찾을 수 있는 사람이 없다** → `stalled` (전부 마지막 술래의 제 구획 것이다 - M11 규칙 4)
 *
 * 세 번째가 새로 생긴 자리다. 셋이서 둘이 탄환을 털리고 남은 하나가 남의 구획을 다 찾으면 그 뒤 5분은
 * 아무도 아무것도 할 수 없는 시간이다 - 시간이 남았다는 것은 라운드가 살아 있다는 뜻이 아니다.
 *
 * **변화가 하나도 없는 판은 여기서 끝내지 않는다.** 고정 평면도로 도는 봇 판·테스트가 수색에 들어가자마자
 * 리빌로 튀면 그 판으로는 아무것도 검증할 수 없다. 찾을 사람이 0명인 것만 그 판에서도 끝이다 -
 * 나머지 둘은 "찾을 것"을 세므로 애초에 변화가 없는 판을 건드리지 않는다.
 */
export function settleHunt(state: MatchState): MatchStep {
  if (state.phase !== 'hunt') return idle(state)
  if (allHuntersOut(state)) return enterReveal(state, 'hunt', 'all-out')
  if (state.changes.length === 0) return idle(state)
  if (unfoundChanges(state).length === 0) return enterReveal(state, 'hunt', 'all-found')
  if (reachableUnfound(state).length === 0) return enterReveal(state, 'hunt', 'stalled')
  return idle(state)
}

/* --- 이벤트 ---------------------------------------------------------------------- */

function onPhaseTimeout(state: MatchState, deps: MatchDeps): MatchStep {
  switch (state.phase) {
    case 'lobby':
    case 'result':
      return idle(state)
    case 'decorate':
      return leaveDecorate(state, deps)
    case 'watch':
      return leaveWatch(state, deps)
    case 'change':
      return finishChange(state, deps)
    case 'hunt':
      return enterReveal(state, 'hunt', 'timeout')
    case 'reveal':
      return enterResult(state, deps)
  }
}

/**
 * 시간이 흐른다. `dtMs = atMs - clockMs`. 뒤로 가는 시계는 던진다 - 호스트의 버그이고, 조용히 0으로 접으면
 * 그 뒤 모든 타이머가 거짓이 된다. 같은 시각은 무시(참조 동일).
 */
export function onTick(state: MatchState, atMs: number, deps: MatchDeps): MatchStep {
  if (!Number.isFinite(atMs)) {
    throw new RangeError(`tick: atMs는 유한수여야 한다 (받은 값: ${String(atMs)})`)
  }
  const dtMs = atMs - state.clockMs
  if (dtMs < 0) {
    throw new RangeError(`tick: 시계가 뒤로 갔다 (clockMs=${String(state.clockMs)}, atMs=${String(atMs)})`)
  }
  if (dtMs === 0) return idle(state)

  let step: MatchStep = {
    state: { ...state, clockMs: atMs, phaseElapsedMs: state.phaseElapsedMs + dtMs },
    effects: [],
  }

  // 원래 방 타이머와 문 예약은 수색 중에만 흐른다. 리빌·결과에서 쿨타임이 도는 것은 의미가 없다.
  // 틱이 수색을 끝내는 길은 마감 하나뿐이다 - 전량 발견·전원 관전은 사격에서만 일어난다(`settleHunt`).
  if (step.state.phase === 'hunt') {
    step = chain(step, (current) => tickOrigin(current, dtMs, deps.tuning))
  }

  const duration = phaseDurationMs(step.state, deps.tuning)
  if (duration === null || step.state.phaseElapsedMs < duration) return step
  return chain(step, (current) => onPhaseTimeout(current, deps))
}

/**
 * 넘어가기에 표를 던진다 - **전원이 눌러야 넘어간다**.
 *
 * 전에는 방장의 것이었다. 그런데 버튼은 전원에게 떴고 남이 누르면 아무 일도 없었다 - 반응 없는 입력은
 * 고장으로 읽힌다(4.3절). 방장만 보이게 감추는 길도 있었지만, 이 버튼이 바꾸는 것은 **모두의 시간**이다.
 * 남은 30초를 아직 쓰고 있는 사람의 라운드를 한 사람이 끝낼 수 있으면 그게 더 큰 사고다.
 *
 * 정족수는 **붙어 있는 사람 좌석**이다(`matchSelectors.skipVotesNeeded`) - 끊긴 사람을 세면 탭 하나 닫은
 * 사람 때문에 남은 판 내내 아무도 못 넘어간다. 표가 안 모여도 페이즈 시계가 대신 끝낸다.
 *
 * 다시 누르면 표를 거둔다. 잘못 눌러 놓고 남이 마저 누르기만 기다려야 하면 그것도 못 되돌리는 입력이다.
 */
export function onSkipVote(state: MatchState, by: PlayerId, deps: MatchDeps): MatchStep {
  // 앞당길 것이 없는 페이즈에서는 표도 세지 않는다(`onSkipPhase`·`matchView`와 같은 목록).
  if (!canSkipPhase(state.phase)) return idle(state)
  const voter = state.players[by]
  if (voter === undefined || voter.kind !== 'human') return idle(state)

  const voted = state.skipVotes.includes(by)
  const next = voted ? state.skipVotes.filter((id) => id !== by) : [...state.skipVotes, by]
  const withVote: MatchState = { ...state, skipVotes: next }

  if (next.length < skipVotesNeeded(state)) {
    // 표만 움직였다. 전원에게 알리는 이유는 화면이 "몇 명이 눌렀나"를 세기 때문이다.
    return { state: withVote, effects: [all({ type: 'skipVotes', votes: next.length, needed: skipVotesNeeded(state) })] }
  }
  // 다 모였다. 표는 `reduceMatch`가 페이즈 경계에서 지운다.
  return onSkipPhase(withVote, deps)
}

/** 페이즈를 앞당긴다. 로비·결과에는 앞당길 것이 없다. */
export function onSkipPhase(state: MatchState, deps: MatchDeps): MatchStep {
  switch (state.phase) {
    case 'lobby':
    case 'result':
      return idle(state)
    case 'decorate':
    case 'watch':
    case 'change':
    case 'reveal':
      return onPhaseTimeout(state, deps)
    case 'hunt':
      return enterReveal(state, 'hunt', 'skipped')
  }
}
